import * as React from 'react';
import { Title } from '../../../title';
import { I18n } from 'react-redux-i18n';
import { ConditionalField } from '../../../conditional-field';
import { FieldPicker, FieldPickerOnChangeValue } from '../../../field-picker';
import { QuestionnaireFolderField } from '@modules/fhq-design-tool/types/field';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { TextField } from '../../../text-field';
import { Select, SELECT_EMPTY_VALUE, SelectValue } from '../../../select';
import { QuestionnaireInactiveFrequencyType } from '@common/dto/questionnaire/frequency';
import { FieldPrimaryListDto } from '@common/dto/field/list-primary-dto';
import { QUESTIONNAIRE_VALIDATION_CONSTANTS, QuestionnaireManagementErrors } from '@modules/fhq-design-tool/controllers/questionnaire-validation';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';

import './incomplete-questionnaires.styles.scss';

export type IncompleteQuestionnairesProps = {
  errors?: QuestionnaireManagementErrors;
  getFields: (params: { folderId?: FolderPrimaryListDto['id'] }) => Promise<QuestionnaireFolderField[]>;
  getFolders: () => Promise<FolderPrimaryListDto[]>;
  questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  onChange: (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => void;
};

export const IncompleteQuestionnaires: React.FC<IncompleteQuestionnairesProps> = (props) => {
  const { onChange, questionnaire, getFields, getFolders, errors } = props;

  const handleUncompletedQuestionnairesChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeUncompletedInactive: val,
        ...(val ? {} : {
          makeInactiveAfter: false,
          makeInactiveAfterDate: false,
          makeInactiveAfterFrequency: 0,
          makeInactiveAfterType: null,
          makeInactiveAfterDateFieldName: '',
          makeInactiveAfterDateFieldId: 0,
        })
      },
    });
  }, [onChange, questionnaire]);

  const handleDateFieldChange = React.useCallback((val: FieldPickerOnChangeValue | undefined) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeInactiveAfterDateFieldId: val ? val.field.id as FieldPrimaryListDto['id'] : undefined,
        makeInactiveAfterDateFieldName: val ? val.field.name : undefined,
      },
    });
  }, [onChange, questionnaire]);

  const handleAfterThisDateChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeInactiveAfterDate: val,
        ...(val ? {} : {
          makeInactiveAfterDateFieldName: '',
          makeInactiveAfterDateFieldId: 0,
        })
      },
    });
  }, [onChange, questionnaire]);

  const handleMakeInactiveAfterChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeInactiveAfter: val,
        ...(val ? {} : {
          makeInactiveAfterFrequency: 0,
          makeInactiveAfterType: null,
        })
      },
    });
  }, [onChange, questionnaire]);

  const handleMakeInactiveAfterAmountChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeInactiveAfterFrequency: Number(val) || 0
      },
    });
  }, [onChange, questionnaire]);

  const handleMakeInactiveAfterTypeChange = React.useCallback((val: SelectValue | null | undefined) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        makeInactiveAfterType: val as QuestionnaireInactiveFrequencyType | undefined
      },
    });
  }, [onChange, questionnaire]);

  return (
    <div className="FhqDesignToolStatusActionsIncompleteQuestionnaires">
      <Title text={I18n.t('Incomplete Questionnaires')} />
      <ConditionalField 
        label={I18n.t('Make incomplete questionnaires inactive')}
        value={questionnaire.statusTab.makeUncompletedInactive}
        onChange={handleUncompletedQuestionnairesChange}
        metadata={{
          description: 'make-uncompleted-questionnaires-inactive'
        }}
      >
        <ConditionalField 
          label={I18n.t('Make Inactive After')}
          value={questionnaire.statusTab.makeInactiveAfter}
          onChange={handleMakeInactiveAfterChange}
          metadata={{
            description: 'make-inactive-after'
          }}
          classes={{
            component: 'FhqDesignToolStatusActionsIncompleteQuestionnaires__make-inactive-children-components'
          }}
        >
          <TextField 
            inputWidth={75}
            margin="12px 12px 0 0"
            metadata={{
              description: 'make-inactive-after-amount'
            }}
            type="number"
            errors={errors?.properties?.makeInactiveAfterFrequency}
            value={questionnaire.statusTab.makeInactiveAfterFrequency?.toString() ?? ''}
            min={QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.min}
            max={QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.max}
            onChange={handleMakeInactiveAfterAmountChange}
            classes={{
              errorText: 'FhqDesignToolStatusActionsIncompleteQuestionnaires__field__errorText'
            }}
          />
          <Select 
            options={[
              { value: QuestionnaireInactiveFrequencyType.days, label: I18n.t('Days') },
              { value: QuestionnaireInactiveFrequencyType.weeks, label: I18n.t('Weeks') },
              { value: QuestionnaireInactiveFrequencyType.months, label: I18n.t('Months') },
            ]}
            value={questionnaire.statusTab.makeInactiveAfterType ?? SELECT_EMPTY_VALUE}
            metadata={{
              description: 'make-inactive-after-type'
            }}
            onChange={handleMakeInactiveAfterTypeChange}
            errors={errors?.properties?.makeInactiveAfterType}
          />
        </ConditionalField>
        <ConditionalField 
          label={I18n.t('After This Date')}
          value={questionnaire.statusTab.makeInactiveAfterDate}
          onChange={handleAfterThisDateChange}
          metadata={{
            description: 'after-this-date'
          }}
        >
          <FieldPicker 
            labelWidth="auto"
            withSearch={true}
            value={questionnaire.statusTab.makeInactiveAfterDateFieldName ?? undefined}
            onChange={handleDateFieldChange}
            metadata={{
              description: 'after-this-date-field',
            }}
            getFields={getFields}
            getFolders={getFolders}
            label={I18n.t('Choose Field')}
            errors={errors?.properties?.makeInactiveAfterDateFieldName}
          />
        </ConditionalField>
      </ConditionalField>
    </div>
  );
};